<template>
  <div class="game-container">
    <MobileView v-if="isMobile" />
    <div v-else-if="!showSummary" class="game-layout">
      <!-- Competencies Section -->
      <div
        class="header-flex"
        style="
          display: flex;
          justify-content: space-between;
          align-items: center;
          width: 100%;
        "
      >
        <div class="header-left">
          <h2 class="font-semibold m-0">
            Competencies
            <button
              class="help-button-inline"
              @click="showInstructions"
              title="Show instructions"
              aria-label="Show instructions"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <circle cx="12" cy="12" r="10"></circle>
                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
                <line x1="12" y1="17" x2="12.01" y2="17"></line>
              </svg>
            </button>
          </h2>
        </div>
        <div class="submit-container">
          <button
            class="submit-button"
            @click="navigateToSummary"
            :disabled="!hasDroppedCompetencies || isFormDisabled"
          >
            Submit & Review
          </button>
        </div>
      </div>
      <!-- Proficiencies Section -->
      <KanbanBoard :isSummary="false" />
    </div>
    <div v-else class="summary-kanban">
      <div class="header-flex">
        <h2 class="font-semibold m-0">
          Summary of Your Choices
          <button
            class="help-button-inline"
            @click="showInstructions"
            title="Show instructions"
            aria-label="Show instructions"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="9.09" y1="9" x2="20" y2="20"></line>
              <path d="m21 21-6-6"></path>
              <path
                d="M10.29 3.86A1 1 0 0 1 11 4v1a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h2"
              ></path>
              <path
                d="M14 3v1a1 1 0 0 0 1 1h2a1 1 0 0 0 1-1V3a1 1 0 0 0-1-1h-2a1 1 0 0 0-1 1"
              ></path>
              <path d="M15 8v2a1 1 0 0 1-1 1H9l-4 4v-7a1 1 0 0 1 1-1h5"></path>
            </svg>
          </button>
        </h2>
        <div class="submit-container">
          <button
            class="back-button"
            @click="showSummary = false"
            :disabled="isFormDisabled"
          >
            Back
          </button>
          <button
            class="submit-button"
            @click="navigateToNextStep"
            :disabled="isFormDisabled"
          >
            Next
          </button>
        </div>
      </div>
      <KanbanBoard :isSummary="true" />
    </div>
  </div>

  <!-- Instructions Modal -->
  <KanbanInstructionsModal
    :is-visible="hasSeenInstructions"
    @close="closeInstructions"
  />
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from "vue";
import { useConsentStatus } from "@/composables/useConsentStatus";
import { useRouter } from "vue-router";
import MobileView from "../views/MobileView.vue";
import Summary from "./Summary.vue";
import KanbanBoard from "./KanbanBoard.vue";
import ViewPicker from "./ViewPicker.vue";
import KanbanInstructionsModal from "./KanbanInstructionsModal.vue";
import { useFirstTimeUser } from "../composables/useFirstTimeUser";
import {
  competencyGroups as originalCompetencyGroups,
  proficiencies,
} from "../stores/constants";
import {
  droppedCompetencies,
  setDroppedCompetencies,
} from "../stores/gameStore";

const { isFormDisabled } = useConsentStatus();
const {
  hasSeenInstructions,
  showInstructions,
  hideInstructions,
  markAsVisited,
} = useFirstTimeUser();

const closeInstructions = () => {
  hideInstructions();
  markAsVisited();
};

const props = defineProps({
  surveyData: {
    type: Object,
    required: true,
  },
  currentStage: Number,
  totalStages: Number,
  nextStageInfo: Object,
});

const emit = defineEmits(["next-stage", "skip-stage"]);

interface Competency {
  competencyTitle: string;
  competencyTitleDescription: string;
  type?: string;
}

interface CompetencyGroup {
  competencyGrouping: string;
  competencyGroupingDescription: string;
  competencies: Competency[];
  type?: string;
}

interface DroppedCompetency {
  competencyTitle: string;
  competencyGrouping: string;
  type?: string;
}

const router = useRouter();
const competencyGroups = ref<CompetencyGroup[]>([...originalCompetencyGroups]);
const collapsedGroups = ref<Record<string, boolean>>(
  Object.fromEntries(
    originalCompetencyGroups.map((group) => [group.competencyGrouping, true])
  )
);

const showSummary = ref(false);
const isMobile = ref(false);

const hasDroppedCompetencies = computed(() => {
  return Object.values(droppedCompetencies.value).some(
    (competencies) => competencies.length > 0
  );
});

const navigateToNextStep = () => {
  // Store competency choices in surveyData before proceeding
  props.surveyData.competencyChoices = droppedCompetencies.value;
  emit("next-stage");
};

const navigateToSummary = () => {
  showSummary.value = true;
};

const checkMobile = () => {
  isMobile.value = window.innerWidth <= 1200;
};

onMounted(() => {
  checkMobile();
  window.addEventListener("resize", checkMobile);
});

onUnmounted(() => {
  window.removeEventListener("resize", checkMobile);
});

const isKanbanView = ref(false);
</script>

<style>
/* Global styles for this route only - disable text selection and scrolling */
* {
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}
.debug-info {
  display: none !important;
}
</style>

<style scoped>
h1,
h2,
h3,
h4 {
  margin: 0;
  padding: 0;
}

.game-container {
  display: flex;
  flex-direction: column;
  width: 100%;
  margin: 0;
  padding: 0;
  border: none;
  /* Avoid creating a nested scroll container: on real mobile browsers this can
     trap touch scrolling and prevent the page/survey container from scrolling. */
  overflow: visible;
}

.game-layout {
  display: flex;
  flex-direction: column;
  gap: 0;
  height: 100%;
  overflow: hidden;
  width: 100%;
  margin: 0;
  flex: 1;
}

.competencies-section {
  flex: 1;
  overflow-y: auto;
  padding-right: 1rem;
  max-height: 45%;
  width: 100%;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  padding: 1rem;
}

.proficiencies-section {
  flex: 1;
  overflow-x: auto;
  overflow-y: hidden;
  padding: 1rem;
  max-height: 45%;
  width: 100%;
  display: flex;
  justify-content: center;
  background: #f0f2f5;
  border-radius: 0.5rem;
}

.competencies-section::-webkit-scrollbar,
.proficiencies-section::-webkit-scrollbar {
  width: 8px;
}

.competencies-section::-webkit-scrollbar-thumb,
.proficiencies-section::-webkit-scrollbar-thumb {
  background-color: #cbd5e0;
  border-radius: 4px;
}

.competencies-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 1rem;
  width: 100%;
}

.competency-group {
  background: #f8fafc;
  padding: 1rem;
  border-radius: 0.5rem;
  border: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  height: fit-content;
}

.competencies-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  /* max-height: 500px; */
  overflow: hidden;
  transition: max-height 0.3s ease-out;
  flex-shrink: 0;
}

.competencies-list.is-collapsed {
  max-height: 0;
  overflow: hidden;
  margin: 0;
  padding: 0;
}

.competency-item {
  padding: 0.75rem;
  background: #ebf8ff;
  border: 2px solid #90cdf4;
  border-radius: 0.375rem;
  cursor: grab;
  transition: all 0.2s ease;
  position: relative;
  padding-left: 2.5rem;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.competency-item:hover {
  background: #bee3f8;
  transform: translateY(-2px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.competency-item:active {
  transform: scale(0.98);
}

.competency-item::after {
  content: "⋮⋮⋮";
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  opacity: 0.5;
  font-size: 1.2rem;
  letter-spacing: 2px;
}

.proficiencies-grid {
  display: flex;
  gap: 1rem;
  padding: 0.5rem;
  height: 100%;
  min-width: min-content;
}

.proficiency-box {
  background: #ebecf0;
  padding: 0.75rem;
  border-radius: 0.375rem;
  border: none;
  transition: all 0.2s ease;
  height: 100%;
  width: 300px;
  min-width: 300px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  margin: 0;
  flex-shrink: 0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
}

.proficiency-box:hover {
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.15);
}

.proficiency-box.drag-over {
  background: #e3f2fd;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.proficiency-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem;
  margin-bottom: 0.5rem;
  background: rgba(255, 255, 255, 0.5);
  border-radius: 0.25rem;
}

.proficiency-header h3 {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #172b4d;
}

.dropped-competencies {
  margin-top: 0.5rem;
  padding: 0.5rem;
  background: rgba(255, 255, 255, 0.5);
  border-radius: 0.25rem;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}

.dropped-competencies h4 {
  margin: 0 0 0.5rem 0;
  font-size: 0.75rem;
  color: #5e6c84;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.dropped-item {
  padding: 0.75rem;
  background: white;
  border-radius: 0.25rem;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
  cursor: grab;
  transition: all 0.2s ease;
  position: relative;
  padding-left: 2.5rem;
  padding-right: 2rem;
  word-wrap: break-word;
  overflow-wrap: break-word;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.dropped-item:hover {
  background: #f8f9fa;
  transform: translateY(-2px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.dropped-item:active {
  transform: scale(0.98);
}

.dropped-item::after {
  content: "⋮⋮";
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  opacity: 0.5;
  font-size: 1rem;
  letter-spacing: 1px;
  color: #5e6c84;
}

.dropped-item.dragging {
  opacity: 0.5;
  background: #e3f2fd;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.group-name {
  color: #5e6c84;
  font-size: 0.75rem;
  font-style: italic;
}

.remove-button {
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  padding: 0.25rem;
  cursor: pointer;
  color: #5e6c84;
  transition: all 0.2s ease;
  border-radius: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  opacity: 0;
}

.dropped-item:hover .remove-button {
  opacity: 1;
}

.remove-button:hover {
  color: #eb5a46;
  background-color: rgba(235, 90, 70, 0.1);
}

.remove-button:active {
  transform: translateY(-50%) scale(0.95);
}

.tooltip-container {
  position: relative;
  display: inline-block;
}

.info-button {
  background: none;
  border: none;
  padding: 0.25rem;
  cursor: pointer;
  color: #5e6c84;
  transition: all 0.2s ease;
  border-radius: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
}

.info-button:hover {
  color: #172b4d;
  background-color: rgba(0, 0, 0, 0.05);
}

.tooltip {
  visibility: hidden;
  position: absolute;
  z-index: 1;
  bottom: 125%;
  left: 50%;
  transform: translateX(-50%);
  background-color: #172b4d;
  color: white;
  text-align: center;
  padding: 0.75rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  width: 250px;
  opacity: 0;
  transition: opacity 0.2s ease;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.tooltip-container:hover .tooltip {
  visibility: visible;
  opacity: 1;
}

.tooltip::after {
  content: "";
  position: absolute;
  top: 100%;
  left: 50%;
  margin-left: -5px;
  border-width: 5px;
  border-style: solid;
  border-color: #172b4d transparent transparent transparent;
}

.header-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding: 0 2rem;
}

.submit-container {
  display: flex;
  justify-content: center;
  gap: 1rem;
}

.submit-button,
.skip-button {
  padding: 0.75rem 2rem;
  border-radius: 0.375rem;
  font-weight: 500;
  transition: all 0.2s ease;
  font-size: 1.125rem;
  min-width: 200px;
}

.submit-button {
  background-color: #005461;
  color: white;
}

.skip-button {
  background-color: #e2e8f0;
  color: #4a5568;
}

.submit-button:hover:not(:disabled) {
  background-color: #005461;
  transform: translateY(-2px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.skip-button:hover {
  background-color: #cbd5e0;
  transform: translateY(-2px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.submit-button:disabled {
  background-color: #cbd5e0;
  cursor: not-allowed;
}

.group-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  position: relative;
  padding-right: 0.5rem;
  cursor: grab;
  transition: all 0.2s ease;
  padding-left: 0;
}

.group-header:hover {
  background-color: #f0f9ff;
  border-radius: 0.25rem;
}

.group-header::after {
  display: none;
}

.trash-button {
  position: absolute;
  top: -15px;
  right: -15px;
  background: none;
  border: none;
  padding: 0.25rem;
  cursor: pointer;
  color: #718096;
  transition: all 0.2s ease;
  border-radius: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
}

.trash-button:hover {
  color: #e53e3e;
  background-color: #fff5f5;
}

.trash-button:active {
  transform: scale(0.95);
}

.group-title-container {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  cursor: pointer;
  flex: 1;
  padding-left: 0.5rem;
  justify-content: flex-start;
  min-height: 2.4em;
}

.group-title {
  margin: 0;
  user-select: none;
  text-align: left;
  flex: 1;
  width: 150px;
  line-height: 1.2;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.toggle-button {
  background: none;
  border: none;
  padding: 0.25rem;
  cursor: pointer;
  color: #718096;
  transition: all 0.2s ease;
  border-radius: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  margin-left: -0.5rem;
}

.toggle-button:hover {
  color: #005461;
  background-color: #ebf8ff;
}

.toggle-button.is-collapsed {
  transform: rotate(-90deg);
}

.proficiency-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.tooltip-container {
  position: relative;
  display: inline-block;
}

.info-button {
  background: none;
  border: none;
  padding: 0.25rem;
  cursor: pointer;
  color: #5e6c84;
  transition: all 0.2s ease;
  border-radius: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
}

.info-button:hover {
  color: #172b4d;
  background-color: rgba(0, 0, 0, 0.05);
}

.tooltip {
  visibility: hidden;
  position: absolute;
  z-index: 1;
  bottom: 125%;
  left: 50%;
  transform: translateX(-50%);
  background-color: #172b4d;
  color: white;
  text-align: center;
  padding: 0.75rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  width: 250px;
  opacity: 0;
  transition: opacity 0.2s ease;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.tooltip-container:hover .tooltip {
  visibility: visible;
  opacity: 1;
}

.tooltip::after {
  content: "";
  position: absolute;
  top: 100%;
  left: 50%;
  margin-left: -5px;
  border-width: 5px;
  border-style: solid;
  border-color: #172b4d transparent transparent transparent;
}

.summary-layout {
  padding: 2rem;
  height: 100%;
  overflow-y: auto;
}

.view-switcher {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
  padding: 0 1rem;
}

.view-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.375rem;
  background: white;
  color: #4a5568;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.view-button:hover {
  background: #f7fafc;
  border-color: #cbd5e0;
}

.view-button.active {
  background: #ebf8ff;
  border-color: #005461;
  color: #2b6cb0;
}

.view-button svg {
  width: 16px;
  height: 16px;
}

.view-picker-container {
  position: sticky;
  top: 0;
  z-index: 10;
  background-color: white;
}

.view-content {
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
}

.grid-view {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1rem;
  padding: 1rem;
}

.competency-group {
  background: white;
  border-radius: 0.5rem;
  padding: 1rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.competency-group h3 {
  margin: 0 0 1rem 0;
  color: var(--text-color);
  font-size: 1.1rem;
}

.competency-items {
  display: grid;
  gap: 0.5rem;
}

.competency-item {
  padding: 0.75rem;
  background: var(--surface-color);
  border-radius: 0.375rem;
  font-size: 0.875rem;
  color: var(--text-color);
}

.list-view {
  max-width: 800px;
  margin: 0 auto;
  padding: 1rem;
}

.list-view .competency-group {
  margin-bottom: 2rem;
}

.list-view .competency-items {
  list-style: none;
  padding: 0;
  margin: 0;
}

.list-view .competency-item {
  padding: 0.75rem;
  border-bottom: 1px solid var(--border-color);
}

.list-view .competency-item:last-child {
  border-bottom: none;
}

.summary-kanban {
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 0;
  padding: 0;
  overflow: hidden;
}

.back-button {
  padding: 0.75rem 2rem;
  border-radius: 0.375rem;
  font-weight: 500;
  transition: all 0.2s ease;
  font-size: 1.125rem;
  min-width: 120px;
  background-color: #e2e8f0;
  color: #4a5568;
  margin-right: 1rem;
}

.back-button:hover {
  background-color: #cbd5e0;
  transform: translateY(-2px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.header-flex {
  position: sticky;
  top: 0;
  z-index: 100;
  background: white;
  padding: 1rem;
  border-bottom: 1px solid #e2e8f0;
  flex-shrink: 0;
}

.header-left {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.filter-checkboxes {
  display: flex;
  gap: 1rem;
  align-items: center;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #4a5568;
  cursor: pointer;
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  background-color: #e2e8f0;
  transition: all 0.2s ease;
  user-select: none;
}

.checkbox-label:hover {
  background-color: #cbd5e0;
  transform: translateY(-1px);
}

.checkbox-label input[type="checkbox"] {
  display: none;
}

.checkbox-label input[type="checkbox"]:checked + span {
  color: #2b6cb0;
  font-weight: 500;
}

.checkbox-label input[type="checkbox"]:checked + span::before {
  content: "✓";
  margin-right: 0.5rem;
  color: #2b6cb0;
}

.checkbox-label input[type="checkbox"]:not(:checked) + span::before {
  content: "○";
  margin-right: 0.5rem;
  color: #4a5568;
}

.checkbox-label span {
  display: flex;
  align-items: center;
}

.competency-item[data-type="Technical"] {
  background: #1b4332;
  color: white;
  border: 2px solid #2d6a4f;
}

.competency-item[data-type="Technical"]:hover {
  background: #2d6a4f;
  transform: translateY(-2px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.dropped-item[data-type="Technical"] {
  background: #1b4332;
  color: white;
  border: 2px solid #2d6a4f;
}

.dropped-item[data-type="Technical"]:hover {
  background: #2d6a4f;
  transform: translateY(-2px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.dropped-item[data-type="Technical"] .group-name {
  color: #d8f3dc;
}

.dropped-item[data-type="Technical"] .remove-button {
  color: #d8f3dc;
}

.dropped-item[data-type="Technical"] .remove-button:hover {
  color: #ff6b6b;
  background-color: rgba(255, 107, 107, 0.1);
}

.help-button-inline {
  background: rgba(0, 0, 0, 0.1);
  border: 2px solid rgba(0, 0, 0, 0.2);
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #374151;
  padding: 0;
  margin-left: 0.75rem;
  vertical-align: middle;
}

.help-button-inline:hover {
  background: rgba(0, 0, 0, 0.2);
  border-color: rgba(0, 0, 0, 0.3);
  transform: scale(1.05);
}

.help-button-inline:active {
  transform: scale(0.95);
}

.help-button-inline svg {
  transition: transform 0.2s ease;
}

.help-button-inline:hover svg {
  transform: rotate(15deg);
}

/* Mobile responsive styles */
@media (max-width: 768px) {
  .header-flex {
    flex-direction: column;
    gap: 1rem;
    padding: 0.75rem;
  }

  .header-left {
    width: 100%;
  }

  .submit-container {
    width: 100%;
    justify-content: stretch;
  }

  .submit-button,
  .back-button {
    flex: 1;
    min-width: auto;
    padding: 0.75rem 1rem;
    font-size: 1rem;
  }

  .back-button {
    margin-right: 0.5rem;
  }
}

@media (max-width: 480px) {
  .header-flex {
    padding: 0.5rem;
  }

  .submit-button,
  .back-button {
    padding: 0.75rem 0.5rem;
    font-size: 0.9375rem;
  }
}
</style>
